// ─────────────────────────────────────────────────────────────────
// Glass-i — Employee login provisioning modal (Stage HRMS-3).
// FOUNDATION: link an existing sub-user, provision a new one (master
// only, via the existing users service), or toggle the login flags.
// No auth logic is re-implemented here.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Descriptions, Divider, Form, Input, Modal, Select, Space, Tag, message
} from 'antd';

import { hrEmployeesApi } from './hrEmployeesApi';

const ROLE_OPTIONS = ['admin', 'manager', 'staff', 'viewer'].map((r) => ({ value: r, label: r }));

export default function EmployeeProvisioningModal({ employeeId, open, onClose }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linkForm] = Form.useForm();
  const [provForm] = Form.useForm();

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try { setState(await hrEmployeesApi.getProvisioning(employeeId)); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to load provisioning.'); }
    finally { setLoading(false); }
  }, [employeeId]);
  useEffect(() => { if (open) { setState(null); linkForm.resetFields(); provForm.resetFields(); load(); } }, [open, load, linkForm, provForm]);

  useEffect(() => {
    if (state && state.suggestedUsername) provForm.setFieldsValue({ username: state.suggestedUsername, role: 'staff' });
  }, [state, provForm]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const next = await fn(); if (next) setState(next); if (okMsg) message.success(okMsg); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Action failed.'); }
    finally { setBusy(false); }
  };

  const doLink = async () => {
    let v; try { v = await linkForm.validateFields(); } catch (_e) { return; }
    run(() => hrEmployeesApi.linkUser(employeeId, v.userId), 'Login user linked.');
  };
  const doProvision = async () => {
    let v; try { v = await provForm.validateFields(); } catch (_e) { return; }
    run(() => hrEmployeesApi.provisionUser(employeeId, v), 'Login user created & linked.');
  };

  const linked = state && state.linked;

  return (
    <Modal open={open} title="Provision Login" onCancel={onClose} footer={null} width={620} destroyOnClose>
      <div style={{ opacity: loading ? 0.6 : 1 }}>
        {state ? (
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}
            items={[
              { key: 'e', label: 'Employee', children: `${state.employeeName} (${state.employeeCode})` },
              { key: 'l', label: 'Linked User', children: state.linkedUser
                  ? <Space><span>{state.linkedUser.username}</span><Tag>{state.linkedUser.role}</Tag><Tag color={state.linkedUser.status === 'active' ? 'green' : 'volcano'}>{state.linkedUser.status}</Tag></Space>
                  : <Tag>none</Tag> },
              { key: 'f', label: 'Flags', children: (
                  <Space size={4}>
                    <Tag color={state.canLogin ? 'blue' : 'default'}>can login: {String(state.canLogin)}</Tag>
                    <Tag color={state.loginEnabled ? 'cyan' : 'default'}>enabled: {String(state.loginEnabled)}</Tag>
                    <Tag color={state.loginLocked ? 'volcano' : 'default'}>locked: {String(state.loginLocked)}</Tag>
                  </Space>) }
            ]} />
        ) : null}

        {state && !state.supportsForcePasswordReset ? (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="First-login password reset is not available yet — share the initial password with the employee. Real account lock/unlock mirrors to the login account when done by the master." />
        ) : null}

        <Space wrap style={{ marginBottom: 12 }}>
          <Button size="small" loading={busy} onClick={() => run(() => hrEmployeesApi.enableLogin(employeeId), 'Login enabled.')}>Enable login</Button>
          <Button size="small" loading={busy} onClick={() => run(() => hrEmployeesApi.disableLogin(employeeId), 'Login disabled.')}>Disable login</Button>
          <Button size="small" loading={busy} onClick={() => run(() => hrEmployeesApi.lockLogin(employeeId), 'Login locked.')}>Lock</Button>
          <Button size="small" loading={busy} onClick={() => run(() => hrEmployeesApi.unlockLogin(employeeId), 'Login unlocked.')}>Unlock</Button>
        </Space>

        {!linked ? (
          <>
            <Divider orientation="left" plain style={{ margin: '6px 0 12px' }}>Link an existing login user</Divider>
            <Form form={linkForm} layout="inline" size="small">
              <Form.Item name="userId" rules={[{ required: true, message: 'User ID (UUID) required.' }]} style={{ flex: 1 }}>
                <Input placeholder="Existing sub-user UUID" style={{ width: 360 }} />
              </Form.Item>
              <Button size="small" type="primary" loading={busy} onClick={doLink}>Link</Button>
            </Form>

            <Divider orientation="left" plain style={{ margin: '16px 0 12px' }}>Or create a new login (master only)</Divider>
            <Form form={provForm} layout="vertical" size="small">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <Form.Item name="username" label="Username"><Input placeholder="e.g. ravi.kumar" /></Form.Item>
                <Form.Item name="role" label="Role" initialValue="staff"><Select options={ROLE_OPTIONS} /></Form.Item>
              </div>
              <Form.Item name="password" label="Initial Password" rules={[{ required: true, message: 'An initial password is required.' }]}>
                <Input.Password placeholder="Strong password (min 12 chars)" />
              </Form.Item>
              <Button size="small" type="primary" loading={busy} onClick={doProvision}>Create &amp; link</Button>
            </Form>
          </>
        ) : (
          <Alert type="success" showIcon message="This employee is linked to a login user. Use the buttons above to enable/disable or lock/unlock." />
        )}
      </div>
    </Modal>
  );
}
